import { lazy, LazyExoticComponent, ComponentType } from 'react'

export interface PreloadableComponent<T extends ComponentType<any>> extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>
}

/**
 * This function can be used to dynamically import a component so that the JS is not loaded until the component is mounted.
 * Note that the component will suspend while the JS loads, so make sure to use the appropriate Suspense boundaries.
 *
 * The returned component also has a `preload` method that can be used to preload the JS, for example by calling it from the `useEffect` hook.
 *
 * @param factory the function to load the component, which should be similar to: () => import('./MyComponent')
 * @returns a React Component that will load its JS on mount and suspends while the JS loads. The component also has a `preload` method that can be used to preload the JS.
 */
export function preloadableLazy<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>): PreloadableComponent<T> {
  return Object.assign(lazy<T>(factory), { preload: factory })
}
